'use client'

import React, { useState, useCallback, useEffect, useRef } from 'react'
import type ThreeJSApp from '@/shared/lib/threejs/app'
import { PerspectiveCamera, DirectionalLight, Color } from 'three'
import { Settings, X, RotateCcw, Copy, Check, Eye } from 'lucide-react'

interface AvatarControlPanelProps {
  app: ThreeJSApp | null
}

interface PanelValues {
  camX: number; camY: number; camZ: number
  rotX: number; rotY: number; rotZ: number
  fov: number
  dirInt: number; dirX: number; dirY: number; dirZ: number; dirColor: string
  ambInt: number; ambColor: string
  hemiInt: number; hemiSkyColor: string; hemiGroundColor: string
  pointInt: number; pointColor: string
  scale: number
  // Bloom & Exposure
  bloomStrength: number; bloomRadius: number; bloomThreshold: number
  blendOpacity: number; blendStrength: number
  exposure: number
}

const RAD2DEG = 180 / Math.PI
const DEG2RAD = Math.PI / 180

function colorToHex(color: Color): string {
  return '#' + color.getHexString()
}

function round(n: number, d: number): number {
  const f = Math.pow(10, d)
  return Math.round(n * f) / f
}

export const AvatarControlPanel = ({ app }: AvatarControlPanelProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [values, setValues] = useState<PanelValues | null>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const initialScaleRef = useRef<{ x: number; y: number; z: number } | null>(null)
  // 패널 열릴 때의 초기 스냅샷 (전후 비교용)
  const snapshotRef = useRef<PanelValues | null>(null)

  // Three.js에서 현재 값 읽기
  const readValues = useCallback((): PanelValues | null => {
    if (!app) return null
    const cam = app.camera.activeCamera
    const dirLight = app.light.lights.find((l): l is DirectionalLight => l instanceof DirectionalLight)
    const ambient = app.light.lights.find((l) => l.type === 'AmbientLight')
    const hemi = app.light.lights.find((l) => l.type === 'HemisphereLight')
    const point = app.light.lights.find((l) => l.type === 'PointLight')
    const model = app.world.getCurrentAvatar()?.getModel()

    if (!initialScaleRef.current && model) {
      initialScaleRef.current = { x: model.scale.x, y: model.scale.y, z: model.scale.z }
    }

    const bloomParams = app.renderer.getBloomParams()
    const blendParams = app.renderer.getBlendParams()
    const exposure = app.renderer.getConfig()?.renderer?.toneMappingExposure ?? 1

    return {
      camX: round(cam.position.x, 3), camY: round(cam.position.y, 3), camZ: round(cam.position.z, 3),
      rotX: round(cam.rotation.x * RAD2DEG, 2), rotY: round(cam.rotation.y * RAD2DEG, 2), rotZ: round(cam.rotation.z * RAD2DEG, 2),
      fov: cam instanceof PerspectiveCamera ? round(cam.fov, 1) : 40,
      dirInt: dirLight ? round(dirLight.intensity, 2) : 1,
      dirX: dirLight ? round(dirLight.position.x, 3) : 0, dirY: dirLight ? round(dirLight.position.y, 3) : 0, dirZ: dirLight ? round(dirLight.position.z, 3) : 0,
      dirColor: dirLight ? colorToHex(dirLight.color) : '#ffffff',
      ambInt: ambient ? round(ambient.intensity, 2) : 0.2, ambColor: ambient ? colorToHex(ambient.color) : '#ffffff',
      hemiInt: hemi ? round(hemi.intensity, 2) : 1.5,
      hemiSkyColor: hemi && 'color' in hemi ? colorToHex(hemi.color) : '#fff2db',
      hemiGroundColor: hemi && 'groundColor' in hemi ? colorToHex((hemi as any).groundColor) : '#d7d0d0',
      pointInt: point ? round(point.intensity, 2) : 10, pointColor: point ? colorToHex(point.color) : '#ffffff',
      scale: model && initialScaleRef.current ? round(model.scale.x / initialScaleRef.current.x, 2) : 1,
      bloomStrength: bloomParams?.strength ?? 0.5,
      bloomRadius: bloomParams?.radius ?? 0,
      bloomThreshold: bloomParams?.threshold ?? 0.8,
      blendOpacity: blendParams?.blendOpacity ?? 0.1,
      blendStrength: blendParams?.bloomStrength ?? 1,
      exposure: round(exposure, 2),
    }
  }, [app])

  const handleOpen = useCallback(() => {
    const v = readValues()
    if (v) {
      setValues(v)
      snapshotRef.current = { ...v }
    }
    setIsOpen(true)
  }, [readValues])

  // 전체 PanelValues를 Three.js에 적용
  const applyAll = useCallback((v: PanelValues) => {
    if (!app) return
    const cam = app.camera.activeCamera
    const dirLight = app.light.lights.find((l): l is DirectionalLight => l instanceof DirectionalLight)
    const ambient = app.light.lights.find((l) => l.type === 'AmbientLight')
    const hemi = app.light.lights.find((l) => l.type === 'HemisphereLight')
    const point = app.light.lights.find((l) => l.type === 'PointLight')
    const model = app.world.getCurrentAvatar()?.getModel()

    // Camera
    cam.position.set(v.camX, v.camY, v.camZ)
    cam.rotation.set(v.rotX * DEG2RAD, v.rotY * DEG2RAD, v.rotZ * DEG2RAD)
    if (cam instanceof PerspectiveCamera) { cam.fov = v.fov; cam.updateProjectionMatrix() }

    // Lights
    if (dirLight) { dirLight.intensity = v.dirInt; dirLight.position.set(v.dirX, v.dirY, v.dirZ); dirLight.color.set(v.dirColor) }
    if (ambient) { ambient.intensity = v.ambInt; ambient.color.set(v.ambColor) }
    if (hemi) { hemi.intensity = v.hemiInt; hemi.color.set(v.hemiSkyColor); if ('groundColor' in hemi) (hemi as any).groundColor.set(v.hemiGroundColor) }
    if (point) { point.intensity = v.pointInt; point.color.set(v.pointColor) }

    // Scale
    if (model && initialScaleRef.current) {
      const s = initialScaleRef.current
      model.scale.set(s.x * v.scale, s.y * v.scale, s.z * v.scale)
    }

    // Bloom
    app.renderer.updateBloomParams({ strength: v.bloomStrength, radius: v.bloomRadius, threshold: v.bloomThreshold })
    app.renderer.updateBlendParams({ blendOpacity: v.blendOpacity, bloomStrength: v.blendStrength })

    // Exposure
    app.renderer.instance.toneMappingExposure = v.exposure

    app.markNeedsUpdate()
  }, [app])

  // 단일 값 변경
  const applyOne = useCallback((key: keyof PanelValues, val: number) => {
    if (!app) return
    const cam = app.camera.activeCamera
    const dirLight = app.light.lights.find((l): l is DirectionalLight => l instanceof DirectionalLight)
    const ambient = app.light.lights.find((l) => l.type === 'AmbientLight')
    const hemi = app.light.lights.find((l) => l.type === 'HemisphereLight')
    const point = app.light.lights.find((l) => l.type === 'PointLight')
    const model = app.world.getCurrentAvatar()?.getModel()

    switch (key) {
      case 'camX': cam.position.x = val; break
      case 'camY': cam.position.y = val; break
      case 'camZ': cam.position.z = val; break
      case 'rotX': cam.rotation.x = val * DEG2RAD; break
      case 'rotY': cam.rotation.y = val * DEG2RAD; break
      case 'rotZ': cam.rotation.z = val * DEG2RAD; break
      case 'fov': if (cam instanceof PerspectiveCamera) { cam.fov = val; cam.updateProjectionMatrix() } break
      case 'dirInt': if (dirLight) dirLight.intensity = val; break
      case 'dirX': if (dirLight) dirLight.position.x = val; break
      case 'dirY': if (dirLight) dirLight.position.y = val; break
      case 'dirZ': if (dirLight) dirLight.position.z = val; break
      case 'ambInt': if (ambient) ambient.intensity = val; break
      case 'hemiInt': if (hemi) hemi.intensity = val; break
      case 'pointInt': if (point) point.intensity = val; break
      case 'scale':
        if (model && initialScaleRef.current) {
          const s = initialScaleRef.current
          model.scale.set(s.x * val, s.y * val, s.z * val)
        }
        break
      case 'bloomStrength': app.renderer.updateBloomParams({ strength: val }); break
      case 'bloomRadius': app.renderer.updateBloomParams({ radius: val }); break
      case 'bloomThreshold': app.renderer.updateBloomParams({ threshold: val }); break
      case 'blendOpacity': app.renderer.updateBlendParams({ blendOpacity: val }); break
      case 'blendStrength': app.renderer.updateBlendParams({ bloomStrength: val }); break
      case 'exposure': app.renderer.instance.toneMappingExposure = val; break
    }
    app.markNeedsUpdate()
  }, [app])

  const applyColor = useCallback((key: keyof PanelValues, hex: string) => {
    if (!app) return
    const dirLight = app.light.lights.find((l): l is DirectionalLight => l instanceof DirectionalLight)
    const ambient = app.light.lights.find((l) => l.type === 'AmbientLight')
    const hemi = app.light.lights.find((l) => l.type === 'HemisphereLight')
    const point = app.light.lights.find((l) => l.type === 'PointLight')
    switch (key) {
      case 'dirColor': if (dirLight) dirLight.color.set(hex); break
      case 'ambColor': if (ambient) ambient.color.set(hex); break
      case 'hemiSkyColor': if (hemi) hemi.color.set(hex); break
      case 'hemiGroundColor': if (hemi && 'groundColor' in hemi) (hemi as any).groundColor.set(hex); break
      case 'pointColor': if (point) point.color.set(hex); break
    }
    app.markNeedsUpdate()
  }, [app])

  const handleChange = useCallback((key: keyof PanelValues, val: number) => {
    setValues((prev) => prev ? { ...prev, [key]: val } : prev)
    applyOne(key, val)
  }, [applyOne])

  const handleColorChange = useCallback((key: keyof PanelValues, hex: string) => {
    setValues((prev) => prev ? { ...prev, [key]: hex } : prev)
    applyColor(key, hex)
  }, [applyColor])

  const handleReset = useCallback(() => {
    if (!snapshotRef.current) return
    const snap = { ...snapshotRef.current }
    setValues(snap)
    applyAll(snap)
  }, [applyAll])

  // ── 전후 비교: 누르는 동안 스냅샷 적용, 떼면 현재값 복원 ──
  const handlePreviewStart = useCallback(() => {
    if (!snapshotRef.current) return
    setIsPreviewing(true)
    applyAll(snapshotRef.current)
  }, [applyAll])

  const handlePreviewEnd = useCallback(() => {
    if (!values) return
    setIsPreviewing(false)
    applyAll(values)
  }, [applyAll, values])

  // 클립보드 복사
  const copyToClipboard = useCallback(() => {
    if (!values) return
    const output = {
      camera: { position: { x: values.camX, y: values.camY, z: values.camZ }, rotation: { x: values.rotX, y: values.rotY, z: values.rotZ }, fov: values.fov },
      directionalLight: { intensity: values.dirInt, color: values.dirColor, position: { x: values.dirX, y: values.dirY, z: values.dirZ } },
      ambientLight: { intensity: values.ambInt, color: values.ambColor },
      hemisphereLight: { intensity: values.hemiInt, skyColor: values.hemiSkyColor, groundColor: values.hemiGroundColor },
      pointLight: { intensity: values.pointInt, color: values.pointColor },
      bloom: { strength: values.bloomStrength, radius: values.bloomRadius, threshold: values.bloomThreshold },
      blend: { opacity: values.blendOpacity, strength: values.blendStrength },
      exposure: values.exposure,
      avatar: { scale: values.scale },
    }
    navigator.clipboard.writeText(JSON.stringify(output, null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }, [values])

  // 단축키
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'c') {
        e.preventDefault()
        copyToClipboard()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, copyToClipboard])

  if (!app) return null

  return (
    <>
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="absolute top-4 right-4 z-[999] p-2.5 rounded-full bg-black/40 backdrop-blur-sm text-white/80 hover:text-white hover:bg-black/60 transition-all pointer-events-auto"
        >
          <Settings size={20} />
        </button>
      )}

      {isOpen && values && (
        <div className="absolute top-4 right-4 z-[999] w-72 max-h-[90vh] overflow-y-auto bg-black/70 backdrop-blur-md rounded-2xl p-4 text-white/90 shadow-2xl scrollbar-thin pointer-events-auto">
          {/* 헤더 */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold tracking-wide">
              {isPreviewing ? '⏪ Before' : 'Avatar Settings'}
            </span>
            <div className="flex gap-1">
              {/* 전후 비교 버튼 */}
              <button
                onPointerDown={handlePreviewStart}
                onPointerUp={handlePreviewEnd}
                onPointerLeave={handlePreviewEnd}
                className={`p-1.5 rounded-lg transition-colors ${isPreviewing ? 'bg-yellow-500/30 text-yellow-300' : 'hover:bg-white/10'}`}
                title="길게 누르면 이전 세팅 미리보기"
              >
                <Eye size={14} />
              </button>
              <button onClick={copyToClipboard} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="값 복사 (⌘⇧C)">
                {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
              </button>
              <button onClick={handleReset} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="새로고침">
                <RotateCcw size={14} />
              </button>
              <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* ── CAM ── */}
          <Section title="CAM" subtitle="position">
            <Row3 labels={['X','Y','Z']} values={[values.camX,values.camY,values.camZ]} keys={['camX','camY','camZ']} ranges={[[-50,50],[-50,50],[-50,100]]} step={0.1} onChange={handleChange} />
          </Section>
          <Section title="CAM" subtitle="rotation (deg)">
            <Row3 labels={['X','Y','Z']} values={[values.rotX,values.rotY,values.rotZ]} keys={['rotX','rotY','rotZ']} ranges={[[-90,90],[-180,180],[-180,180]]} step={0.5} onChange={handleChange} />
          </Section>
          <Section title="CAM" subtitle="fov">
            <SliderRow value={values.fov} min={10} max={120} step={1} onChange={(v) => handleChange('fov', v)} />
          </Section>

          <Divider />

          {/* ── DIR ── */}
          <Section title="DIR" subtitle="intensity + color">
            <div className="flex gap-2 items-end">
              <div className="flex-1"><SliderRow value={values.dirInt} min={0} max={5} step={0.05} onChange={(v) => handleChange('dirInt', v)} /></div>
              <ColorPicker value={values.dirColor} onChange={(c) => handleColorChange('dirColor', c)} />
            </div>
          </Section>
          <Section title="DIR" subtitle="position">
            <Row3 labels={['X','Y','Z']} values={[values.dirX,values.dirY,values.dirZ]} keys={['dirX','dirY','dirZ']} ranges={[[-30,30],[-30,30],[-30,30]]} step={0.1} onChange={handleChange} />
          </Section>

          <Divider />

          {/* ── AMB ── */}
          <Section title="AMB" subtitle="intensity + color">
            <div className="flex gap-2 items-end">
              <div className="flex-1"><SliderRow value={values.ambInt} min={0} max={3} step={0.05} onChange={(v) => handleChange('ambInt', v)} /></div>
              <ColorPicker value={values.ambColor} onChange={(c) => handleColorChange('ambColor', c)} />
            </div>
          </Section>

          <Divider />

          {/* ── HEMI ── */}
          <Section title="HEMI" subtitle="intensity + colors">
            <SliderRow value={values.hemiInt} min={0} max={5} step={0.05} onChange={(v) => handleChange('hemiInt', v)} />
            <div className="flex gap-3 mt-1.5">
              <div className="flex items-center gap-1.5"><span className="text-[10px] text-white/40">sky</span><ColorPicker value={values.hemiSkyColor} onChange={(c) => handleColorChange('hemiSkyColor', c)} /></div>
              <div className="flex items-center gap-1.5"><span className="text-[10px] text-white/40">gnd</span><ColorPicker value={values.hemiGroundColor} onChange={(c) => handleColorChange('hemiGroundColor', c)} /></div>
            </div>
          </Section>

          <Divider />

          {/* ── POINT ── */}
          <Section title="POINT" subtitle="intensity + color">
            <div className="flex gap-2 items-end">
              <div className="flex-1"><SliderRow value={values.pointInt} min={0} max={30} step={0.5} onChange={(v) => handleChange('pointInt', v)} /></div>
              <ColorPicker value={values.pointColor} onChange={(c) => handleColorChange('pointColor', c)} />
            </div>
          </Section>

          <Divider />

          {/* ── BLOOM ── */}
          <Section title="BLOOM" subtitle="post-processing">
            <Row3
              labels={['str', 'rad', 'thr']}
              values={[values.bloomStrength, values.bloomRadius, values.bloomThreshold]}
              keys={['bloomStrength', 'bloomRadius', 'bloomThreshold']}
              ranges={[[0, 3], [0, 1], [0, 1]]}
              step={0.01}
              onChange={handleChange}
            />
          </Section>

          {/* ── BLEND ── */}
          <Section title="BLEND" subtitle="bloom mix">
            <div className="grid grid-cols-2 gap-1.5">
              <div>
                <div className="flex justify-between items-center gap-1 text-[10px] mb-0.5"><span className="text-white/50">opacity</span><NumberField value={values.blendOpacity} onCommit={(v) => handleChange('blendOpacity', v)} /></div>
                <input type="range" min={0} max={1} step={0.01} value={values.blendOpacity} onChange={(e) => handleChange('blendOpacity', parseFloat(e.target.value))} className={SLIDER_CLS} />
              </div>
              <div>
                <div className="flex justify-between items-center gap-1 text-[10px] mb-0.5"><span className="text-white/50">strength</span><NumberField value={values.blendStrength} onCommit={(v) => handleChange('blendStrength', v)} /></div>
                <input type="range" min={0} max={3} step={0.01} value={values.blendStrength} onChange={(e) => handleChange('blendStrength', parseFloat(e.target.value))} className={SLIDER_CLS} />
              </div>
            </div>
          </Section>

          {/* ── EXPOSURE ── */}
          <Section title="EXPOSURE" subtitle="tone mapping">
            <SliderRow value={values.exposure} min={0} max={3} step={0.01} onChange={(v) => handleChange('exposure', v)} />
          </Section>

          <Divider />

          {/* ── AVATAR ── */}
          <Section title="AVATAR" subtitle="scale">
            <SliderRow value={values.scale} min={0.1} max={3} step={0.01} onChange={(v) => handleChange('scale', v)} />
          </Section>

          <div className="mt-3 pt-2 border-t border-white/10 text-[10px] text-white/30 text-center">
            ⌘⇧C 복사 &nbsp;|&nbsp; 👁 길게 누르면 이전값 미리보기
          </div>
        </div>
      )}
    </>
  )
}

// ── Sub Components ──

const SLIDER_CLS = "w-full h-0.5 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-sm"

const Section = ({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) => (
  <div className="mb-3">
    <div className="flex items-baseline gap-1.5 mb-1.5">
      <span className="text-[11px] font-bold text-blue-400 tracking-wider">{title}</span>
      <span className="text-[10px] text-white/40">{subtitle}</span>
    </div>
    {children}
  </div>
)

const Divider = () => <div className="border-t border-white/5 mb-3" />

interface Row3Props {
  labels: [string, string, string]
  values: [number, number, number]
  keys: [keyof PanelValues, keyof PanelValues, keyof PanelValues]
  ranges: [[number, number], [number, number], [number, number]]
  step: number
  onChange: (key: keyof PanelValues, val: number) => void
}

/** 수치 직접 입력 필드 — 슬라이더 옆 숫자 표시를 편집 가능한 input으로 */
const NUM_INPUT_CLS = "w-14 bg-white/10 border border-white/15 rounded px-1 py-px text-[10px] text-right text-white/80 tabular-nums outline-none focus:border-blue-400/60 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"

const NumberField = ({ value, onCommit }: { value: number; onCommit: (v: number) => void }) => {
  const [text, setText] = useState(String(value))
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    if (!editing) setText(String(value))
  }, [value, editing])

  const commit = () => {
    setEditing(false)
    const parsed = parseFloat(text)
    if (!Number.isNaN(parsed)) onCommit(parsed)
    else setText(String(value))
  }

  return (
    <input
      type="number"
      value={text}
      onFocus={() => setEditing(true)}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
      className={NUM_INPUT_CLS}
    />
  )
}

const Row3 = ({ labels, values, keys, ranges, step, onChange }: Row3Props) => (
  <div className="grid grid-cols-3 gap-1.5">
    {labels.map((label, i) => (
      <div key={label}>
        <div className="flex justify-between items-center gap-1 text-[10px] mb-0.5">
          <span className="text-white/50">{label}</span>
          <NumberField value={values[i]} onCommit={(v) => onChange(keys[i], v)} />
        </div>
        <input type="range" min={ranges[i][0]} max={ranges[i][1]} step={step} value={values[i]} onChange={(e) => onChange(keys[i], parseFloat(e.target.value))} className={SLIDER_CLS} />
      </div>
    ))}
  </div>
)

const SliderRow = ({ value, min, max, step, onChange }: { value: number; min: number; max: number; step: number; onChange: (v: number) => void }) => (
  <div>
    <div className="flex justify-end text-[10px] mb-0.5">
      <NumberField value={value} onCommit={onChange} />
    </div>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className={SLIDER_CLS} />
  </div>
)

const ColorPicker = ({ value, onChange }: { value: string; onChange: (hex: string) => void }) => (
  <div className="relative flex-shrink-0">
    <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
      className="w-6 h-6 rounded-md border border-white/20 cursor-pointer bg-transparent p-0 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-none" />
  </div>
)
